<template>
  <div id="academic_interdisciplinary">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc" :color="this.page.color"></usc-hero>
    <usc-intro :copy="this.intro"></usc-intro>
    <div class="body gutter-lg mb">
        <p><i>From Siri to smart bots and beyond, artificial intelligence (AI) is not only increasingly part of our lives but is also revolutionizing commerce. The pioneering AI program blends core business concepts with big data knowledge to foster the strategic, communication and programming skills necessary for continued leadership in an ever-evolving technological landscape. BUAI students will graduate able to turn complicated concepts into success for industries spanning healthcare, finance, manufacturing, marketing, entertainment, public policy and more.</i></p>
        <p>The program only enrolls 50 students a year to ensure collaboration and close mentorship from USC’s expert faculty. Each cohort is led by Professor Kimon Drakopoulos, a globally renowned authority in complex networked systems, game theory, financial modeling, information economics and other AI-related disciplines. He is committed to helping students explore their industry interests to find the ideal starting point for their careers.</p>
        <p>Whether you want to lead a consulting team, become a chief data officer or launch your own startup, if you love quantitative problem solving, the BUAI program could be right for you.</p>
    </div>
    <div class="body gutter-lg mb">
      <h2>Core Courses</h2>
      <p class="p-heading"><strong>Marshall courses (42 units) build solid foundations in:</strong></p>
      <ul>
     <li>Accounting</li>
<li>Economics</li>
<li>Marketing</li>
<li>Leadership and organizational behavior</li>
<li>Strategy</li>
<li>Communications
</li></ul>
<p class="p-heading"><strong>Viterbi courses (36 units) focus on:</strong></p>
      <ul>
     <li>Machine learning</li>
<li>Human-AI interactions</li>
<li>Robotics</li>
<li>Data science</li>
<li>Programming</li>
<li>Ethics in technology
</li></ul>
<p class="p-heading">Meanwhile, the joint degree’s four integrative classes bring it all together to provide full context for making business decisions in the AI age. These courses, specifically created by Marshall for this degree cover:</p>

     <ul >
           <li>Foundation in AI and business principles</li>
        <li>Digital transformation</li>
        <li>AI and smart contracts</li>
        <li>Technology strategy</li>
      </ul>
    </div>
    <usc-course-chart :dataSet="courseData">
      <img slot="image" src="../../../static/USC_Marshall_CurriculumMap_2023_BUAI_R3.png" alt="Course chart for cinematic arts artificial intelligence for business">
    </usc-course-chart>
  </div>
</template>

<script>
import hero from '@/components/Hero'
import intro from '@/components/Intro'
import table from '@/components/Table'
import Cta from '@/components/Cta'
import CourseChart from '@/components/CourseChart/CourseChart'

export default {
  name: 'AIB',
  components: {
    'usc-hero': hero,
    'usc-intro': intro,
    'usc-table': table,
    'usc-cta': Cta,
    'usc-course-chart': CourseChart
  },
  data () {
    return {
      page: {
        color: '#ffcc00'
      },
      hero: {
        title: 'Artificial Intelligence for Business',
        loResSrc: '../../../static/HeroImages/Marshall111.jpg',
        hiResSrc: '../../../static/HeroImages/Marshall111.jpg'
      },
      intro: [
        'Offered in partnership with the top-ranked USC Viterbi School of Engineering, our newest joint degree program, the Bachelor of Science in Artificial Intelligence for Business, prepares you to bridge business with computing to open up a range of world-changing prospects. You’ll engage in this dynamic field with hands-on, real project learning and interact with some of the top minds from Silicon Beach.'
      ],
      table1: {
        title: 'BCA internships include:',
        data: [
          {
            left: '20th Century Fox',
            right: 'ABC Comedy Development'
          },
          {
            left: 'ABC/Jimmy Kimmel Live',
            right: 'Bravo'
          },
          {
            left: 'Fox Sports Networks',
            right: 'Paramount Pictures'
          },
          {
            left: 'United Talent Agency (UTA)',
            right: ''
          }
        ]
      },
      table2: {
        title: 'Full-Time Employment:',
        data: [
          {
            left: 'Creative Arts Agency (CAA)',
            right: 'William Morris Endeavor (WME)'
          },
          {
            left: 'United Talent Agency (UTA)',
            right: '20th Century Fox'
          },
          {
            left: 'Warner Bros.',
            right: 'Sony Music Entertainment'
          },
          {
            left: 'Google',
            right: 'Paramount Pictures'
          }
        ],
        disclaimer: ''
      },
      courseData: {
        title: 'USC Marshall Artificial Intelligence for Business Program',
        subtitle: 'Four-Year Sample Course Sequence',
        key: [
          {
            courseColor: 'marshall-core',
            courseLabel: 'Marshall Core'
          },
          {
            courseColor: 'usc-elective',
            courseLabel: 'USC Elective/Minor'
          },
          {
            courseColor: 'usc-requirement',
            courseLabel: 'USC Requirement'
          },
          {
            courseColor: 'ai',
            courseLabel: 'AI Core'
          }
        ],
        years: {
          freshman: {
            yearTitle: 'FRESHMAN',
            semesters: {
              Fall: [
                {
                  courseID: 'Math 125',
                  courseName: 'Calculus',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 312',
                  courseName: 'Stats & Data Science for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'WRIT 150',
                  courseName: 'Writing and Critical Reasoning',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'CSCI 102',
                  courseName: 'Fundamentals of Computation',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                },
                {
                  courseID: 'BUAD 112',
                  courseName: 'AI for Business Freshman Academy',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ],
              Spring: [
                {
                  courseID: 'ECON 351x',
                  courseName: 'Microeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 304',
                  courseName: 'Organizational Behavior and Leadership',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '4',
                  courseCredits: '4'
                },
                {
                  courseID: 'GESM',
                  courseName: 'General Education Seminar',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'CSCI 103',
                  courseName: 'Introduction to Programming',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Optional Elective',
                  courseName: 'BUAD 104',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ]
            }
          },
          sophmore: {
            yearTitle: 'SOPHOMORE',
            semesters: {
              Fall: [
                {
                  courseID: 'BUAD 280',
                  courseName: 'Introduction to Financial Accounting',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'BUAD 302',
                  courseName: 'Communication Strategy in Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'DSCI 281',
                  courseName: 'Foundations of AI & Machine Learning',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'CSCI 170',
                  courseName: 'Discrete Meethods in Computer Science',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'ITP 115 or 116',
                  courseName: 'Programming in Python',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 307',
                  courseName: 'Marketing Fundamentals',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 313',
                  courseName: 'Advanced Operations Management & Analytics',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 306 or 308',
                  courseName: 'Business Finance or Advanced Business Finance',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'CSCI 104',
                  courseName: 'Data Structures and Object Oriented Design',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ]
            }
          },
          junior: {
            yearTitle: 'JUNIOR',
            semesters: {
              Fall: [
                {
                  courseID: 'BUAD 352x',
                  courseName: 'Macroeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 281',
                  courseName: 'Introduction to Managerial Accounting',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'CSCI 360',
                  courseName: 'Introduction to Artificial Intelligence',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'DSO 429',
                  courseName: 'Digital Transformation of Business AI and Smart Contracts',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'MOR 458',
                  courseName: 'Technology Strategy: The Case of AI',
                  courseColor: 'ai',
                  hasMin: true,
                  min: 2,
                  courseCredits: 4
                }
              ],
              Spring: [
                {
                  courseID: 'BUAD 497',
                  courseName: 'Strategic Management',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'CSCI 445',
                  courseName: 'Intro to Robotics',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'WRIT 340',
                  courseName: 'Advanced Writing for Business',
                  courseColor: 'usc-requirement',
                  hasMin: true,
                  min: 3,
                  courseCredits: 4
                },
                {
                  courseID: 'DSO 488',
                  courseName: 'Hands on AI for Business',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ]
            }
          },
          senior: {
            yearTitle: 'SENIOR',
            semesters: {
              Fall: [
                {
                  courseID: 'EIS 370',
                  courseName: 'Ethnical Issues in Artificial Intelligence',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'ITP 459',
                  courseName: 'Applied Machine Learning for Natural Language Processing',
                  courseColor: 'ai',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                }
              ],
              Spring: [
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Free Elective',
                  courseName: '',
                  courseColor: 'usc-elective',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ]
            }
          }
        }
      }
    }
  },
  created () {
    this.$root.setPageHeader('Academics', 'AI For Business')
  }
}
</script>

<style scoped lang="scss">
ul li {
  list-style-type: disc;
  margin-left: 20px;
  padding-left: 20px;
}
</style>



// WEBPACK FOOTER //
// src/pages/academics/AiForBusiness.vue